/**
 * Cuts: shapes taken out of a mark. Each is an item of its own (`t: 'cut'`, `of` the mark) — a
 * square, a circle, a drawn shape, handled and edited as any shape while the mark is being cut into.
 * The mark is drawn, hit and read as itself less its cut regions: the cuts' outlines, the ones that
 * overlap joined as one (so shapes merge to cut, each still editable on its own). A region may lie
 * wholly inside the mark (a hole) or across its edge (a notch): drawing clips either way.
 *
 * Pure, in image pixels.
 */
import type { Cut, Item, Shape, Vec } from '../../shared/doc.ts';
import { differencePolygons, polygonsOverlap, shapeToPolygon, unionPolygons } from './outline.ts';
import { pointInPoly } from './geom.ts';

/** The cuts of a mark, in the order made. */
export function cutsOf(items: readonly Item[], markId: string): Cut[] {
  return items.filter((it): it is Cut => it.t === 'cut' && it.of === markId);
}

const ids = new WeakMap<Shape, number>();
let nextId = 1;
const regionCache = new Map<string, number[][]>();
/** A key for a set of shapes (documents are immutable: the same shapes are the same objects). */
function keyOf(shapes: readonly Shape[]): string {
  return shapes
    .map((s) => {
      let k = ids.get(s);
      if (k === undefined) ids.set(s, (k = nextId++));
      return k;
    })
    .join(',');
}

/** The regions cut out of a mark: its cuts' outlines, those that overlap joined into one (each a closed polygon). */
export function cutRegions(cuts: readonly Cut[]): number[][] {
  if (!cuts.length) return [];
  const shapes = cuts.map((c) => c.shape);
  const key = keyOf(shapes);
  const known = regionCache.get(key);
  if (known) return known;
  const polys = shapes.map(shapeToPolygon).filter((p) => p.length >= 6);
  // the ones that overlap, gathered (union–find)
  const parent = polys.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
  for (let i = 0; i < polys.length; i++) for (let j = i + 1; j < polys.length; j++) if (find(i) !== find(j) && polygonsOverlap(polys[i]!, polys[j]!)) parent[find(i)] = find(j);
  const groups = new Map<number, number[][]>();
  polys.forEach((p, i) => {
    const r = find(i);
    groups.set(r, [...(groups.get(r) ?? []), p]);
  });
  const regions = [...groups.values()].map((g) => (g.length === 1 ? g[0]! : unionPolygons(g))).filter((p) => p.length >= 6);
  regionCache.set(key, regions);
  if (regionCache.size > 256) regionCache.delete(regionCache.keys().next().value!);
  return regions;
}

/** Is a point in a cut region (not on the mark)? */
export function inCut(regions: readonly number[][], p: Vec): boolean {
  return regions.some((r) => pointInPoly(p, r));
}

const outlineCache = new WeakMap<Shape, { key: string; poly: number[] }>();
/**
 * A mark's outline as it reads with its cuts: its border less the regions cut across it (its largest
 * piece; holes wholly inside leave the border as it is). Where arrows aim, and the side a box is on.
 */
export function outlineWithCuts(shape: Shape, cuts: readonly Cut[]): number[] {
  const outer = shapeToPolygon(shape);
  if (!cuts.length) return outer;
  const key = keyOf(cuts.map((c) => c.shape));
  const known = outlineCache.get(shape);
  if (known && known.key === key) return known.poly;
  const pieces = differencePolygons(outer, cutRegions(cuts));
  const poly = pieces[0] ?? outer;
  outlineCache.set(shape, { key, poly });
  return poly;
}
